import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, apiWithAuth } from '../lib/api';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { ALL_PREFECTURES } from '../constants/prefectures';
import { getCitiesByPrefecture } from '../constants/cities';

const EmployerProfileSetup = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    business_name: user?.business_name || '',
    business_type: user?.business_type || '',
    contact_person: user?.name || '',
    contact_phone: user?.phone || '',
    contact_email: user?.email || '',
    prefecture: user?.prefecture || '',
    city: user?.city || '',
    address: user?.address || '',
    business_description: '',
    establishment_year: '',
    employee_count: '',
    website_url: '',
    business_hours: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // エラークリア
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.business_name.trim()) {
      newErrors.business_name = '事業者名・会社名は必須です';
    }
    if (!formData.business_type.trim()) {
      newErrors.business_type = '事業種別は必須です';
    }
    if (!formData.contact_person.trim()) {
      newErrors.contact_person = '担当者名は必須です';
    }
    if (!formData.contact_phone.trim()) {
      newErrors.contact_phone = '連絡先電話番号は必須です';
    }
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = '連絡先メールアドレスは必須です';
    }
    if (!formData.prefecture) {
      newErrors.prefecture = '都道府県は必須です';
    }
    if (!formData.city) {
      newErrors.city = '市区町村は必須です';
    }
    if (!formData.address.trim()) {
      newErrors.address = '住所は必須です';
    }
    if (!formData.business_description.trim()) {
      newErrors.business_description = '事業内容・店舗紹介は必須です';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('すべての必須項目を入力してください');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      // ユーザー情報を更新
      const authApi = apiWithAuth(token);
      const userUpdateResponse = await authApi.put('/mypage/profile', {
        name: formData.contact_person,
        phone: formData.contact_phone,
        prefecture: formData.prefecture,
        city: formData.city,
        business_name: formData.business_name,
        business_type: formData.business_type
      });

      if (userUpdateResponse.status !== 200) {
        throw new Error(userUpdateResponse.data?.detail || 'ユーザー情報の更新に失敗しました');
      }

      // 事業者プロフィールを作成
      const profileData = {
        business_name: formData.business_name,
        description: formData.business_description,
        location: `${formData.prefecture}${formData.city}${formData.address}`,
        prefecture: formData.prefecture,
        city: formData.city,
        address: formData.address,
        categories: [formData.business_type],
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        establishment_year: formData.establishment_year || null,
        employee_count: formData.employee_count || null,
        website_url: formData.website_url || null,
        business_hours: formData.business_hours || null
      };
      const profileResponse = await authApi.post('/employer/profile', profileData);

      if (profileResponse.status !== 201 && profileResponse.status !== 200) {
        throw new Error(profileResponse.data?.detail || 'プロフィールの作成に失敗しました');
      }

      const profileResult = profileResponse.data;
      const updatedUser = userUpdateResponse.data;
      updateUser(updatedUser);
      toast.success('事業者プロフィールを保存しました！');
      navigate('/employer-dashboard');

    } catch (error) {
      console.error('Profile setup error:', error);
      toast.error('プロフィールの保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };


  const [businessTypes, setBusinessTypes] = useState([
    '飲食店・レストラン',
    '小売店・コンビニ',
    'イベント・エンターテイメント',
    'オフィス・事務',
    '清掃・メンテナンス',
    '配送・物流',
    'その他'
  ]); // Default fallback

  // Fetch categories on mount
  React.useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await fetch(`${API_BASE}/categories`);
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (data.categories || []);
          // Extract labels
          const labels = list.map(c => c.label || c.name);
          if (labels.length > 0) {
            setBusinessTypes(labels);
          }
        }
      } catch (err) {
        console.error("Failed to load categories for setup", err);
      }
    };
    fetchCats();
  }, []);


  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ヘッダー */}
        <div className="text-center mb-8">
          <div className="bg-yellow-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <BuildingOfficeIcon className="h-8 w-8 text-yellow-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">事業者プロフィール設定</h1>
          <p className="text-gray-600 mt-2">
            事業者情報を入力して、プロフィールを完成させましょう
          </p>
        </div>

        {/* プログレスバー */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full">
                  <CheckCircleIcon className="h-5 w-5 text-white" />
                </div>
                <span className="ml-2 text-sm font-medium text-green-600">アカウント作成</span>
              </div>
              <div className="w-16 h-1 bg-yellow-200 rounded-full">
                <div className="w-full h-full bg-yellow-500 rounded-full"></div>
              </div>
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 bg-yellow-500 rounded-full">
                  <span className="text-white text-sm font-medium">2</span>
                </div>
                <span className="ml-2 text-sm font-medium text-yellow-600">プロフィール設定</span>
              </div>
              <div className="w-16 h-1 bg-gray-200 rounded-full"></div>
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full">
                  <span className="text-gray-500 text-sm font-medium">3</span>
                </div>
                <span className="ml-2 text-sm font-medium text-gray-500">完了</span>
              </div>
            </div>
          </div>
        </div>

        {/* フォーム */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* 事業者基本情報 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 mr-2 text-yellow-600" />
                  事業者基本情報
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      事業者名・会社名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${errors.business_name ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="株式会社サンプル"
                    />
                    {errors.business_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.business_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      事業種別 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="business_type"
                      value={formData.business_type}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${errors.business_type ? 'border-red-300' : 'border-gray-300'
                        }`}
                    >
                      <option value="">選択してください</option>
                      {businessTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {errors.business_type && (
                      <p className="text-red-500 text-sm mt-1">{errors.business_type}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      設立年
                    </label>
                    <input
                      type="number"
                      name="establishment_year"
                      value={formData.establishment_year}
                      onChange={handleChange}
                      min="1900"
                      max="2024"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      placeholder="2020"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      従業員数
                    </label>
                    <select
                      name="employee_count"
                      value={formData.employee_count}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    >
                      <option value="">選択してください</option>
                      <option value="1-5">1-5名</option>
                      <option value="6-20">6-20名</option>
                      <option value="21-50">21-50名</option>
                      <option value="51-100">51-100名</option>
                      <option value="100+">100名以上</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ウェブサイトURL
                    </label>
                    <input
                      type="url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </div>

              {/* 連絡先情報 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <PhoneIcon className="h-5 w-5 mr-2 text-blue-600" />
                  連絡先情報
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      担当者名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${errors.contact_person ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="山田太郎"
                    />
                    {errors.contact_person && (
                      <p className="text-red-500 text-sm mt-1">{errors.contact_person}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      連絡先電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${errors.contact_phone ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="03-1234-5678"
                    />
                    {errors.contact_phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.contact_phone}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      連絡先メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${errors.contact_email ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="contact@example.com"
                    />
                    {errors.contact_email && (
                      <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 所在地情報 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <MapPinIcon className="h-5 w-5 mr-2 text-green-600" />
                  所在地情報
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      都道府県 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="prefecture"
                      value={formData.prefecture}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${errors.prefecture ? 'border-red-300' : 'border-gray-300'
                        }`}
                    >
                      <option value="">選択してください</option>
                      {ALL_PREFECTURES.map((prefecture) => (
                        <option key={prefecture.name} value={prefecture.name}>
                          {prefecture.name}
                        </option>
                      ))}
                    </select>
                    {errors.prefecture && (
                      <p className="text-red-500 text-sm mt-1">{errors.prefecture}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      市区町村 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      disabled={!formData.prefecture}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${errors.city ? 'border-red-300' : 'border-gray-300'
                        }`}
                    >
                      <option value="">選択してください</option>
                      {formData.prefecture &&
                        getCitiesByPrefecture(formData.prefecture).map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                    </select>
                    {errors.city && (
                      <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      住所 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${errors.address ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="1-1-1 ビル名 階数"
                    />
                    {errors.address && (
                      <p className="text-red-500 text-sm mt-1">{errors.address}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 事業内容・営業時間 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-purple-600" />
                  事業内容・店舗情報
                </h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      事業内容・店舗紹介 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="business_description"
                      value={formData.business_description}
                      onChange={handleChange}
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${errors.business_description ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="事業内容や店舗の特徴、働く環境について詳しく記載してください..."
                    />
                    {errors.business_description && (
                      <p className="text-red-500 text-sm mt-1">{errors.business_description}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      営業時間
                    </label>
                    <input
                      type="text"
                      name="business_hours"
                      value={formData.business_hours}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      placeholder="平日 9:00-18:00、土日 10:00-17:00"
                    />
                  </div>
                </div>
              </div>

              {/* ボタン */}
              <div className="flex gap-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/employer-dashboard')}
                  className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  後で設定する
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-yellow-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-yellow-700 transition-colors disabled:opacity-50"
                >
                  {loading ? '保存中...' : 'プロフィールを保存する'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerProfileSetup;